import { Location } from '@angular/common';
import { Injectable, NgZone } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Params,
  Router,
} from '@angular/router';
import { isArray, isString, last, takeRight } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, pairwise, shareReplay, tap } from 'rxjs/operators';
import { UrlService } from '../url/url.service';
import { LoggerService } from '../logger/logger.service';
import { WindowRefService } from '../window/window-ref.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private _nativeWindow: any;
  private _previousUrls: string[] = [];
  private _lastUrlFromBackAction: string;
  private _navigationEnds: Subject<string> = new Subject<string>();

  navigationEnds$: Observable<string> = this._navigationEnds
    .asObservable()
    .pipe(
      tap((url: string) => this._loggerService.log('navigating to:', url)),
      shareReplay(1)
    );

  routeData$: BehaviorSubject<any> = new BehaviorSubject<any>({});

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _urlService: UrlService,
    private _loggerService: LoggerService,
    private _windowRefService: WindowRefService,
    private _ngZone: NgZone
  ) {
    this._nativeWindow = this._windowRefService.nativeWindow;

    _router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: any) => event.urlAfterRedirects),
        tap((url) => this._navigationEnds.next(url)),
        pairwise()
      )
      .subscribe(([previousUrl, _]: string[]) => {
        // - only update previousUrls on NavigationEnd so guards don't affect the stack;
        this._pushPreviousUrls(previousUrl);

        this._lastUrlFromBackAction = null;
      });

    this.navigationEnds$.subscribe(() => {
      this._nativeWindow.scrollTo(0, 0);
      this.routeData$.next(this._getDataFromActiveRoute());
    });
  }

  get activeRouteName(): string {
    return this._routeNameFromData(this.routeData$.value);
  }

  get activeRouteName$(): Observable<string> {
    return this.routeData$.pipe(
      map((data) => this._routeNameFromData(data)),
      shareReplay(1)
    );
  }

  private _routeNameFromData(data: any): string {
    const name = data.name;
    const prefix = data.prefixName;
    return prefix ? `${prefix}_${name}` : name;
  }

  // adapted from:
  // https://stackoverflow.com/questions/40863664/how-to-get-current-route-custom-data-in-angular-2
  private _getDataFromActiveRoute() {
    let child = this._route;
    let data = { ...child.snapshot.data };
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.snapshot.data };
      } else if (child.snapshot.data) {
        return data;
      } else {
        return null;
      }
    }
    return null;
  }

  refresh() {
    this._windowRefService.nativeWindow.location.reload();
  }

  goTo(
    route: string | string[],
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    // fix for APPS-763
    // https://stackoverflow.com/questions/48341085/angular-change-detection-not-triggering-on-router-navigate
    return this._ngZone.run(() =>
      this._router.navigate(isArray(route) ? route : [route], navigationExtras)
    );
  }

  goToByUrl(
    url: string,
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    // fix for APPS-763
    // https://stackoverflow.com/questions/48341085/angular-change-detection-not-triggering-on-router-navigate
    return this._ngZone.run(() =>
      this._router.navigateByUrl(url, navigationExtras)
    );
  }

  goBack(
    goBackUrl?: string | string[],
    navigationExtras?: NavigationExtras
  ): Promise<boolean> {
    const lastUrl = goBackUrl || this.getLastUrl();

    this._lastUrlFromBackAction = this.getCurrentUrl();

    if (lastUrl && isString(lastUrl)) {
      const goBackRoute = this._urlService.parseUrlToRoute(
        lastUrl,
        navigationExtras
      );

      return new Promise((resolve) => {
        this.goTo(goBackRoute.route, goBackRoute.navigationExtras); // deactivation guard breaks the Location.back()
        // history see
        // https://github.com/angular/angular/issues/13586
        resolve();
      });
    } else {
      return this.goTo(lastUrl || '/', navigationExtras);
    }
  }

  goToExternalUrl(url: string | string[], newTab: boolean = false) {
    url = isArray(url) ? Location.stripTrailingSlash(url.join('/')) : url;

    if (newTab) {
      this._nativeWindow.open(url, '_blank');
    } else {
      this._nativeWindow.location.href = url;
    }
  }

  getLocationQueryParams(asString?: boolean): Params | string {
    const queryParams: Params = this._urlService.parse(this.getCurrentUrl())
      .queryParams;

    return asString
      ? this._urlService.serializeParams(queryParams)
      : queryParams;
  }

  isRouteActive(
    route: string[],
    navigationExtras?: NavigationExtras,
    exact?: boolean
  ): boolean {
    return this._router.isActive(
      this._router.createUrlTree(route, navigationExtras || {}),
      exact
    );
  }

  getCurrentUrl(): string {
    return this._router.url;
  }

  getLocationPathname(): string {
    return this._nativeWindow.location.pathname;
  }

  getLocationHref(): string {
    return this._nativeWindow.location.href;
  }

  getLocationOrigin(): string {
    return this._nativeWindow.location.origin;
  }

  getLastUrl(): string {
    return last(this._previousUrls);
  }

  getLastUrlFromBackAction(): string {
    return this._lastUrlFromBackAction;
  }

  getPreviousUrls(): string[] {
    return this._previousUrls;
  }

  private _pushPreviousUrls(url: string) {
    this._previousUrls.push(url);

    if (this._previousUrls.length > 100) {
      this._previousUrls = takeRight(this._previousUrls, 50);
    }
  }
}
